import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { clamp } from './mathUtils';
import type { QualityProfile } from './quality';

/**
 * Kapselt WebGLRenderer, Szene, Licht und Environment-Map.
 * Kennt die Spiellogik nicht.
 */
export class Renderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly sun: THREE.DirectionalLight;

  private profile: QualityProfile;
  private renderScale = 1;
  private basePixelRatio = 1;
  private viewportWidth = 1;
  private viewportHeight = 1;
  private pmrem: THREE.PMREMGenerator | null = null;
  private envRT: THREE.WebGLRenderTarget | null = null;

  constructor(canvas: HTMLCanvasElement, profile: QualityProfile) {
    this.profile = profile;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: profile.antialias,
      powerPreference: 'high-performance',
      stencil: false,
      depth: true,
      // Wichtig fuer Mobile: kein Alpha-Backbuffer, spart Bandbreite.
      alpha: false,
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.enabled = profile.shadows;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.info.autoReset = false;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x8ec5e8);
    this.scene.fog = new THREE.Fog(0x9fcbe8, profile.farPlane * 0.35, profile.farPlane * 0.95);

    this.camera = new THREE.PerspectiveCamera(60, 1, 0.25, profile.farPlane);
    this.camera.position.set(0, 6, -10);

    // Sonne. Der Schattenfrustum wird pro Frame um das Fahrzeug herum
    // nachgefuehrt (siehe updateSunTarget) - eine Kaskade reicht fuer M1,
    // echte CSM kommt mit der grossen Welt in M4.
    this.sun = new THREE.DirectionalLight(0xfff2df, 2.6);
    this.sun.position.set(60, 90, 40);
    this.sun.castShadow = profile.shadows;
    this.sun.shadow.mapSize.set(profile.shadowMapSize, profile.shadowMapSize);
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = 260;
    this.sun.shadow.bias = -0.0008;
    this.sun.shadow.normalBias = 0.03;
    this.setShadowExtent(38);
    this.scene.add(this.sun);
    this.scene.add(this.sun.target);

    const hemi = new THREE.HemisphereLight(0xbfd9ef, 0x5a5145, 1.1);
    this.scene.add(hemi);

    this.buildEnvironment();
  }

  /** Prozedurale Environment-Map fuer PBR-Reflexionen - kein Download noetig. */
  private buildEnvironment(): void {
    this.pmrem = new THREE.PMREMGenerator(this.renderer);
    this.pmrem.compileEquirectangularShader();
    const env = new RoomEnvironment();
    this.envRT = this.pmrem.fromScene(env, 0.04);
    this.scene.environment = this.envRT.texture;
    this.scene.environmentIntensity = 0.55;
    env.dispose();
  }

  private setShadowExtent(half: number): void {
    const cam = this.sun.shadow.camera;
    cam.left = -half;
    cam.right = half;
    cam.top = half;
    cam.bottom = -half;
    cam.updateProjectionMatrix();
  }

  /** Haelt den Schattenfrustum um den Spieler zentriert. */
  updateSunTarget(focus: THREE.Vector3): void {
    if (!this.profile.shadows) return;
    this.sun.target.position.copy(focus);
    this.sun.position.set(focus.x + 60, focus.y + 90, focus.z + 40);
    this.sun.target.updateMatrixWorld();
  }

  setProfile(profile: QualityProfile): void {
    this.profile = profile;
    this.renderer.shadowMap.enabled = profile.shadows;
    this.renderer.shadowMap.needsUpdate = true;
    this.sun.castShadow = profile.shadows;
    this.sun.shadow.mapSize.set(profile.shadowMapSize, profile.shadowMapSize);
    this.sun.shadow.map?.dispose();
    this.sun.shadow.map = null;
    this.camera.far = profile.farPlane;
    this.camera.updateProjectionMatrix();
    if (this.scene.fog instanceof THREE.Fog) {
      this.scene.fog.near = profile.farPlane * 0.35;
      this.scene.fog.far = profile.farPlane * 0.95;
    }
    this.resize();
  }

  resize(): void {
    const viewport = window.visualViewport;
    const w = Math.max(1, Math.round(viewport?.width ?? window.innerWidth));
    const h = Math.max(1, Math.round(viewport?.height ?? window.innerHeight));
    this.viewportWidth = w;
    this.viewportHeight = h;
    this.basePixelRatio = clamp(window.devicePixelRatio || 1, 1, this.profile.maxPixelRatio);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(this.basePixelRatio * this.renderScale);
    this.renderer.setSize(w, h, false);
  }

  /**
   * Dynamische Aufloesungsskalierung: lieber leicht weicher als ruckelig.
   * Wird vom Loop anhand der geglaetteten fps aufgerufen.
   */
  adaptRenderScale(smoothedFps: number, dt: number): void {
    const min = this.profile.minRenderScale;
    let target = this.renderScale;
    if (smoothedFps < 50) target -= dt * 0.35;
    else if (smoothedFps > 58) target += dt * 0.12;

    const next = clamp(target, min, 1);
    if (Math.abs(next - this.renderScale) > 0.004) {
      this.renderScale = next;
      this.renderer.setPixelRatio(this.basePixelRatio * this.renderScale);
      this.renderer.setSize(this.viewportWidth, this.viewportHeight, false);
    }
  }

  get currentRenderScale(): number {
    return this.renderScale;
  }

  render(): void {
    this.renderer.info.reset();
    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    this.envRT?.dispose();
    this.pmrem?.dispose();
    this.renderer.dispose();
  }
}
